/**
 * Utility functions for code nesting operations
 */

// Maximum allowed nesting depth for codes
export const MAX_TOTAL_DEPTH = 3;

/**
 * Checks if nesting a code is allowed based on depth constraints
 * 
 * @param {number} maxDepth - The maximum depth of the code being nested (includes its own children)
 * @param {number} targetLevel - The level of the target parent code
 * @returns {boolean} - Whether nesting is allowed
 */
export function isNestingAllowed(maxDepth, targetLevel) {
  // The new depth would be maxDepth + targetLevel
  // If a code with children (maxDepth > 0) is placed under a parent at level > 1,
  // we need to ensure the total depth doesn't exceed MAX_TOTAL_DEPTH
  return maxDepth + targetLevel < MAX_TOTAL_DEPTH;
}

/**
 * Calculates if a code can be dropped on a parent based on depth constraints
 *
 * @param {number} codeMaxDepth - The maximum depth of the code being nested
 * @param {number} parentLevel - The level of the potential parent code
 * @returns {boolean} - Whether the code can be dropped on this parent
 */
export function canDropOnParent(codeMaxDepth, parentLevel) {
  return isNestingAllowed(codeMaxDepth, parentLevel);
}

/**
 * Validates if a code can be merged into another code
 * Merge is only allowed when the dragged code has no children (maxDepth === 0)
 *
 * @param {Object} draggedItem - The item being dragged (contains maxDepth)
 * @returns {boolean} - Whether the merge is valid
 */
export function isValidMerge(draggedItem) {
  // Only allow merging codes that have no children
  return draggedItem && draggedItem.maxDepth === 0;
}
